import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { ROSTER_ROLE_LABEL, rolePlural } from '@nu/shared';

// Who gets a message: everyone, whole roles, whole teams, and/or people
// picked from the list. Roles and teams are kept as such, so people who join
// later can be sent it from the message's page.
export const NO_TARGETS = Object.freeze({ everyone: false, roles: [], teams: [], people: [] });

// The people the targets reach now (the server works it out again on send).
export function reached(audience, t) {
  if (!audience) return [];
  return audience.people.filter((p) => t.everyone || t.roles.includes(p.role) || (p.teamId && t.teams.includes(p.teamId)) || t.people.includes(p.id));
}

const PAGE = 25;
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export default function RecipientPicker({ audience, targets: t, onChange, peopleOnly = false, exclude = [] }) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const out = new Set(exclude);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (audience?.people || []).filter((p) => !out.has(p.id)
      && (!s || p.name.toLowerCase().includes(s) || p.email.toLowerCase().includes(s))
      && (!filter || (filter.startsWith('role:') ? p.role === filter.slice(5) : String(p.teamId) === filter.slice(5))));
  }, [audience, q, filter, exclude.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!audience) return <p className="muted">Loading people…</p>;

  const pages = Math.max(1, Math.ceil(shown.length / PAGE));
  const at = Math.min(page, pages - 1);
  const rows = shown.slice(at * PAGE, at * PAGE + PAGE);
  const covered = (p) => t.everyone || t.roles.includes(p.role) || (p.teamId && t.teams.includes(p.teamId));
  const allShown = shown.length > 0 && shown.every((p) => covered(p) || t.people.includes(p.id));
  const selectShown = () => {
    const ids = shown.filter((p) => !covered(p)).map((p) => p.id);
    onChange({ ...t, people: allShown ? t.people.filter((id) => !ids.includes(id)) : [...new Set([...t.people, ...ids])] });
  };

  return (
    <div className="picker">
      {!peopleOnly && (
        <>
          <label className="check"><input type="checkbox" checked={t.everyone} onChange={(e) => onChange({ ...t, everyone: e.target.checked })} /> Everyone ({audience.people.length})</label>
          {!t.everyone && (
            <>
              <div className="picker-group">
                <span className="small muted">Roles</span>
                <div className="chips">
                  {audience.roles.map((r) => {
                    const n = audience.people.filter((p) => p.role === r.value).length;
                    return <button key={r.value} type="button" className={`chip${t.roles.includes(r.value) ? ' on' : ''}`} aria-pressed={t.roles.includes(r.value)} onClick={() => onChange({ ...t, roles: toggle(t.roles, r.value) })}>{rolePlural(r.label)} ({n})</button>;
                  })}
                </div>
              </div>
              {audience.teams.length > 0 && (
                <div className="picker-group">
                  <span className="small muted">Teams</span>
                  <div className="chips">
                    {audience.teams.map((tm) => {
                      const n = audience.people.filter((p) => p.teamId === tm.id).length;
                      return <button key={tm.id} type="button" className={`chip${t.teams.includes(tm.id) ? ' on' : ''}`} aria-pressed={t.teams.includes(tm.id)} onClick={() => onChange({ ...t, teams: toggle(t.teams, tm.id) })}>{tm.name} ({n})</button>;
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
      {!t.everyone && (
        <div className="picker-group">
          {!peopleOnly && <span className="small muted">People</span>}
          <div className="filters">
            <label className="search">
              <Search size={18} aria-hidden="true" />
              <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search by name or email" aria-label="Search people" />
            </label>
            <select className="popup-input picker-filter" value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }} aria-label="Show">
              <option value="">Everyone</option>
              <optgroup label="Role">{audience.roles.map((r) => <option key={r.value} value={`role:${r.value}`}>{rolePlural(r.label)}</option>)}</optgroup>
              {audience.teams.length > 0 && <optgroup label="Team">{audience.teams.map((tm) => <option key={tm.id} value={`team:${tm.id}`}>{tm.name}</option>)}</optgroup>}
            </select>
          </div>
          <div className="table-scroll">
            <table className="table picker-table">
              <thead>
                <tr>
                  <th className="pick-col"><input type="checkbox" checked={allShown} disabled={!shown.length} onChange={selectShown} aria-label={`Select all ${shown.length} shown`} /></th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Team</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const via = covered(p);
                  return (
                    <tr key={p.id} className={via ? 'inactive' : ''}>
                      <td className="pick-col">
                        <input type="checkbox" checked={via || t.people.includes(p.id)} disabled={via} aria-label={`Send to ${p.name}`} onChange={() => onChange({ ...t, people: toggle(t.people, p.id) })} />
                      </td>
                      <td><strong>{p.name}</strong><div className="small muted">{p.email}</div></td>
                      <td>{ROSTER_ROLE_LABEL[p.role] || p.role}</td>
                      <td>{p.teamName || <span className="muted">None</span>}</td>
                    </tr>
                  );
                })}
                {!rows.length && <tr><td colSpan={4} className="muted">Nobody matches.</td></tr>}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="pager">
              <button type="button" className="btn" disabled={at === 0} onClick={() => setPage(at - 1)}>Previous</button>
              <span className="small muted">Page {at + 1} of {pages} · {shown.length} people</span>
              <button type="button" className="btn" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>Next</button>
            </div>
          )}
          {!allShown && shown.length > 0 && <button type="button" className="link-btn" onClick={selectShown}>Select all {shown.length} shown</button>}
        </div>
      )}
    </div>
  );
}
